<template>
  <td
    class="ra-previous-cell"
    aria-readonly="true"
    tabindex="-1"
  >
    <!-- 参照専用列。今回の入力値と誤認されないようグレー背景・ラベル常時表示とし、操作系は一切描画しない（FR-006-2 / DR-007）。
         コメントを root 要素の外に置くと Vue が fragment 扱いにして属性を参照できなくなるため、内側に置く。 -->
    <div class="ra-previous-cell__label">
      前回の選択肢
    </div>
    <div class="ra-previous-cell__value">
      {{ answerLabel }}
    </div>
    <v-tooltip
      v-if="details.length > 0"
      :disabled="!isTruncated"
      location="left"
    >
      <template #activator="{ props: tooltipProps }">
        <ul
          v-bind="tooltipProps"
          class="ra-previous-cell__details"
        >
          <li
            v-for="detail in visibleDetails"
            :key="detail.key"
          >
            ・{{ detail.label }}
          </li>
          <li v-if="omittedCount > 0">
            ほか {{ omittedCount }} 件
          </li>
        </ul>
      </template>
      <span>{{ details.map((detail) => detail.label).join(" / ") }}</span>
    </v-tooltip>
  </td>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
  ANSWER_TYPES,
  type RiskAssessmentAnswer,
  type RiskAssessmentQuestion,
  type RiskAssessmentQuestionDetail
} from "@/vue/entities/risk-assessment";

const NO_HISTORY_LABEL = "履歴なし";
// 設問改訂で YAML から削除された detail_key が過去データに残るケース（question-catalog.md 1）。
const RETIRED_DETAIL_LABEL = "（廃止された選択肢）";
const MAX_VISIBLE_DETAILS = 4;
const TRUNCATED_DETAIL_COUNT = 3;

const ANSWER_LABELS: Record<number, string> = {
  [ANSWER_TYPES.applicable]: "該当",
  [ANSWER_TYPES.notApplicable]: "非該当",
  [ANSWER_TYPES.unknown]: "不明"
};

const props = defineProps<{
  question: RiskAssessmentQuestion;
  answer: RiskAssessmentAnswer | null;
}>();

type DetailView = { key: string; label: string };

// 入れ子サブ項目（妊娠Ver Q17）は親グループが違っても葉のラベルが重複するため、
// 親ラベルを前置して対応関係を識別できるようにする（FR-006-2 / DR-006）。
const flatten = (details: RiskAssessmentQuestionDetail[], parentLabel = ""): DetailView[] =>
  details.flatMap((detail) =>
    detail.children.length > 0
      ? flatten(detail.children, detail.label)
      : [{ key: detail.key, label: parentLabel ? `${parentLabel}：${detail.label}` : detail.label }]
  );

const detailLabelMap = computed<Record<string, string>>(() =>
  Object.fromEntries(flatten(props.question.details).map((detail) => [detail.key, detail.label]))
);

const answerLabel = computed<string>(() => {
  if (!props.answer) return NO_HISTORY_LABEL;

  return ANSWER_LABELS[props.answer.answerType] ?? NO_HISTORY_LABEL;
});

// 「該当」で前回サブ項目が選択されていた場合のみ列挙する（未選択のサブ項目は表示しない）。
const details = computed<DetailView[]>(() => {
  if (!props.answer || props.answer.answerType !== ANSWER_TYPES.applicable) return [];

  return props.answer.detailKeys.map((key) => ({ key, label: detailLabelMap.value[key] ?? RETIRED_DETAIL_LABEL }));
});

const isTruncated = computed<boolean>(() => details.value.length > MAX_VISIBLE_DETAILS);

const visibleDetails = computed<DetailView[]>(() =>
  isTruncated.value ? details.value.slice(0, TRUNCATED_DETAIL_COUNT) : details.value
);

const omittedCount = computed<number>(() =>
  isTruncated.value ? details.value.length - TRUNCATED_DETAIL_COUNT : 0
);
</script>

<style lang="scss" scoped>
.ra-previous-cell {
  background-color: #e0e0e0;
  color: rgba(0, 0, 0, 0.7);
  vertical-align: middle;
  text-align: center;
  padding: 8px;
}

.ra-previous-cell__label {
  font-size: 10px;
  color: rgba(0, 0, 0, 0.55);
}

.ra-previous-cell__value {
  font-size: 13px;
  font-weight: bold;
}

.ra-previous-cell__details {
  list-style: none;
  padding: 0;
  margin: 4px 0 0;
  font-size: 11px;
  text-align: left;
}
</style>
